import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { Close, WarningAmber } from "@mui/icons-material";
import { useState } from "react";
import { deleteParent as deleteParentApi } from "apis";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

interface Props {
  open: boolean;
  onClose: () => void;
  item: { id: number; name: string };
}

const DeleteUserDialog: React.FC<Props> = ({ open, onClose, item }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [deleting, setDeleting] = useState(false);

  const handleDelete = () => {
    setDeleting(true);
    deleteParentApi({ id: item.id })
      .then(() => {
        enqueueSnackbar(t("parents.deleted"), { variant: "success" });
        onClose();
      })
      .catch((err) => {
        enqueueSnackbar(err?.response?.data?.message || "Error", {
          variant: "error",
        });
      })
      .finally(() => setDeleting(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
          color: "common.white",
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <WarningAmber sx={{ fontSize: 24 }} />
          <Typography variant="subtitle1" fontWeight={700}>
            {t("title.delete_parent")}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "common.white" }}
        >
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, py: 3, textAlign: "center" }}>
        <Typography variant="body1" mb={1}>
          {t("alert.confirm_delete")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>{item.name}</strong>
        </Typography>
      </DialogContent>

      <Box sx={{ px: 3, pb: 3, display: "flex", gap: 1.5 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          fullWidth
          sx={{
            borderRadius: 2,
            py: 1.2,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {t("buttons.cancel")}
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          fullWidth
          disabled={deleting}
          sx={{
            borderRadius: 2,
            py: 1.2,
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
            "&:hover": { boxShadow: 2 },
          }}
        >
          {deleting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t("buttons.delete")
          )}
        </Button>
      </Box>
    </Dialog>
  );
};

export default DeleteUserDialog;
